// frontend/src/components/company/EditDriverModal.jsx
import { useState, useEffect } from 'react';
import { driverAPI } from '../../services/api';

const EditDriverModal = ({ isOpen, driver, onClose, onSuccess }) => {
  // ============================================
  // STATE
  // ============================================
  const [formData, setFormData] = useState({
    driver_name: '',
    phone: '',
    email: '',
    password: '',
    password_confirm: '',
    license_number: '',
    vehicle_number: '',
    vehicle_type: '',
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // ============================================
  // LOAD DRIVER DATA
  // ============================================
  useEffect(() => {
    if (driver) {
      setFormData({
        driver_name: driver.driver_name || '',
        phone: driver.phone || '',
        email: driver.email || '',
        password: '',
        password_confirm: '',
        license_number: driver.license_number || '',
        vehicle_number: driver.vehicle_number || '',
        vehicle_type: driver.vehicle_type || '',
        is_active: driver.is_active !== false
      });
    }
  }, [driver]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.driver_name.trim()) {
      newErrors.driver_name = 'Жолоочийн нэр заавал оруулна уу';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Утасны дугаар заавал оруулна уу';
    } else if (!/^[0-9]{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Утасны дугаар 8 оронтой тоо байх ёстой';
    }

    // Password validation (if changing)
    if (changePassword) {
      if (!formData.password) {
        newErrors.password = 'Нууц үг заавал оруулна уу';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Нууц үг 6-аас дээш тэмдэгт байх ёстой';
      }

      if (formData.password !== formData.password_confirm) {
        newErrors.password_confirm = 'Нууц үг таарахгүй байна';
      }
    }

    // Email validation (optional)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'И-мэйл хаяг буруу байна';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        driver_name: formData.driver_name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        license_number: formData.license_number.trim() || null,
        vehicle_number: formData.vehicle_number.trim() || null,
        vehicle_type: formData.vehicle_type.trim() || null,
        is_active: formData.is_active
      };

      // Only include password if changing
      if (changePassword && formData.password) {
        submitData.password = formData.password;
      }

      await driverAPI.update(driver.id, submitData);

      // Success
      alert('✅ Жолоочийн мэдээлэл амжилттай шинэчлэгдлээ!');
      onSuccess();
    } catch (err) {
      console.error('❌ Update driver error:', err);
      
      const errorMessage = err.response?.data?.message || 'Жолооч засахад алдаа гарлаа';
      
      // Check for specific errors
      if (errorMessage.includes('утасны дугаар')) {
        setErrors({ phone: errorMessage });
      } else {
        alert('❌ ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    
    // Check if form has changes
    const hasChanges = 
      formData.driver_name !== (driver.driver_name || '') ||
      formData.phone !== (driver.phone || '') ||
      formData.email !== (driver.email || '') ||
      formData.license_number !== (driver.license_number || '') ||
      formData.vehicle_number !== (driver.vehicle_number || '') ||
      formData.vehicle_type !== (driver.vehicle_type || '') ||
      formData.is_active !== (driver.is_active !== false) ||
      changePassword;
    
    if (hasChanges) {
      if (!confirm('Өөрчлөлтүүд хадгалагдахгүй. Гарах уу?')) {
        return;
      }
    }
    
    onClose();
  };

  if (!isOpen || !driver) return null;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 px-6 py-5 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <span className="text-3xl">✏️</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Жолооч засах
                  </h2>
                  <p className="text-yellow-100 text-sm mt-0.5">
                    {driver.driver_name} - {driver.phone}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-5">
              {/* ============================================ */}
              {/* SECTION 1: Үндсэн мэдээлэл */}
              {/* ============================================ */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="text-sm font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <span className="text-lg">👤</span>
                  Үндсэн мэдээлэл
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Driver Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Жолоочийн нэр <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="driver_name"
                      value={formData.driver_name}
                      onChange={handleChange}
                      placeholder="Жолоочийн бүтэн нэр"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition ${
                        errors.driver_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.driver_name && (
                      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        {errors.driver_name}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Утасны дугаар <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="99119911"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      maxLength={8}
                    />
                    {errors.phone && (
                      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      И-мэйл
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="driver@example.com"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* License Number */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Жолоочийн үнэмлэхний дугаар
                    </label>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleChange}
                      placeholder="УБ12345678"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                    />
                  </div>

                  {/* Active Status */}
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          Идэвхтэй байх
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          formData.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {formData.is_active ? '✓ Идэвхтэй' : '✗ Идэвхгүй'}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* ============================================ */}
              {/* SECTION 2: Нууц үг солих */}
              {/* ============================================ */}
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-green-900 flex items-center gap-2">
                    <span className="text-lg">🔐</span>
                    Нууц үг солих
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={(e) => {
                        setChangePassword(e.target.checked);
                        if (!e.target.checked) {
                          setFormData({ ...formData, password: '', password_confirm: '' });
                          setErrors({ ...errors, password: '', password_confirm: '' });
                        }
                      }}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 font-medium">Нууц үг солих</span>
                  </label>
                </div>
                
                {changePassword && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Password */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Шинэ нууц үг <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition pr-12 ${
                            errors.password ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <span>⚠️</span>
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {/* Password Confirm */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Нууц үг давтах <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password_confirm"
                        value={formData.password_confirm}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition ${
                          errors.password_confirm ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.password_confirm && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <span>⚠️</span>
                          {errors.password_confirm}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!changePassword && (
                  <p className="text-sm text-gray-600 italic">
                    Нууц үг солихгүй бол чекбоксыг идэвхжүүлнэ үү
                  </p>
                )}
              </div>

              {/* ============================================ */}
              {/* SECTION 3: Машины мэдээлэл */}
              {/* ============================================ */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <h3 className="text-sm font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <span className="text-lg">🚗</span>
                  Машины мэдээлэл
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Vehicle Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Машины дугаар
                    </label>
                    <input
                      type="text"
                      name="vehicle_number"
                      value={formData.vehicle_number}
                      onChange={handleChange}
                      placeholder="УБ1234АА"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition uppercase"
                    />
                  </div>

                  {/* Vehicle Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Машины төрөл
                    </label>
                    <select
                      name="vehicle_type"
                      value={formData.vehicle_type}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
                    >
                      <option value="">Сонгох</option>
                      <option value="Мотоцикл">🏍️ Мотоцикл</option>
                      <option value="Суудлын машин">🚗 Суудлын машин</option>
                      <option value="Ачааны машин">🚚 Ачааны машин</option>
                      <option value="Жижиг ачааны">🚐 Жижиг ачааны</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Stats Info (Read-only) */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-lg">📊</span>
                  Статистик
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {driver.total_deliveries || 0}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Нийт хүргэлт</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {driver.successful_deliveries || 0}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Амжилттай</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {driver.rating ? parseFloat(driver.rating).toFixed(1) : '0.0'}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Үнэлгээ</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-sm font-bold px-3 py-1 rounded-full inline-block ${
                      driver.status === 'available' ? 'bg-green-100 text-green-700' :
                      driver.status === 'busy' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {driver.status === 'available' ? '🟢 Бэлэн' :
                       driver.status === 'busy' ? '🟡 Завгүй' :
                       '⚫ Offline'}
                    </div>
                    <div className="text-xs text-gray-600 font-medium mt-1">Одоогийн статус</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Болих
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-gradient-to-r from-yellow-600 to-yellow-500 text-white font-semibold rounded-lg hover:from-yellow-700 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Хадгалж байна...
                  </>
                ) : (
                  <>
                    <span className="text-lg">💾</span>
                    Хадгалах
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditDriverModal;